import type { TagType, TagStyle } from "@/components/Tag/Tag";
import type { FacilityGovernanceRole, GovernanceTitle } from "./types";
import { GovernanceRole, GovernanceStatus } from "./types";

export const GOVERNANCE_ROLE_NOTE_TITLE = "Governance Role Added";
export const GOVERNANCE_ROLE_UPDATED_NOTE_TITLE = "Governance Role Updated";

const GOVERNANCE_ROLE_LABELS: Record<GovernanceRole, string> = {
  [GovernanceRole.BOARD_MEMBER]: "Board Member",
  [GovernanceRole.COMMITTEE_MEMBER]: "Committee Member",
  [GovernanceRole.SELECTION_COMMITTEE_MEMBER]: "Selection Committee Member",
  [GovernanceRole.CLUB_OFFICER]: "Club Officer",
  [GovernanceRole.OWNER]: "Owner"
};

const GOVERNANCE_STATUS_TAG_TYPES: Record<GovernanceStatus, TagType> = {
  [GovernanceStatus.ACTIVE]: "success",
  [GovernanceStatus.ARCHIVED]: "surface"
};

const GOVERNANCE_STATUS_LABELS: Record<GovernanceStatus, string> = {
  [GovernanceStatus.ACTIVE]: "Active",
  [GovernanceStatus.ARCHIVED]: "Archived"
};

export function formatGovernanceRole(role: GovernanceRole): string {
  return GOVERNANCE_ROLE_LABELS[role];
}

export function mapGovernanceStatus(status: string): GovernanceStatus {
  switch (status.toLowerCase()) {
    case "archived":
      return GovernanceStatus.ARCHIVED;
    case "active":
      return GovernanceStatus.ACTIVE;
    default:
      return GovernanceStatus.ACTIVE;
  }
}

export function formatSectionDisplayName(sectionName: string | null | undefined): string | undefined {
  if (!sectionName?.trim()) {
    return undefined;
  }

  const trimmed = sectionName.trim();

  return /\ssection$/i.test(trimmed) ? trimmed : `${trimmed} Section`;
}

export function getGovernanceStatusLabel(status: GovernanceStatus): string {
  return GOVERNANCE_STATUS_LABELS[status];
}

export function getGovernanceStatusTagType(status: GovernanceStatus): TagType {
  return GOVERNANCE_STATUS_TAG_TYPES[status];
}

export function getGovernanceStatusTagStyle(_status: GovernanceStatus): TagStyle {
  return "strong";
}

export function buildGovernanceTitleOptions(titles: GovernanceTitle[] | undefined): { value: string; label: string }[] {
  return (titles ?? []).map((governanceTitle) => ({ value: governanceTitle.id, label: governanceTitle.title }));
}

interface GovernanceTitleFieldStateArgs {
  loading: boolean;
  hasError: boolean;
  optionCount: number;
}

interface GovernanceTitleFieldState {
  disabled: boolean;
  errorText?: string;
  supportingText?: string;
}

export function getGovernanceTitleFieldState({
  loading,
  hasError,
  optionCount
}: GovernanceTitleFieldStateArgs): GovernanceTitleFieldState {
  if (loading) {
    return { disabled: true, supportingText: "Loading roles…" };
  }

  if (hasError) {
    return { disabled: true, errorText: "Unable to load roles. Please try again." };
  }

  if (optionCount === 0) {
    return { disabled: true, supportingText: "No roles available." };
  }

  return { disabled: false };
}

export function matchesGovernanceSearch(role: FacilityGovernanceRole, searchTerm: string): boolean {
  const normalizedSearch = searchTerm.trim().toLowerCase();

  if (!normalizedSearch) {
    return true;
  }

  const searchableValues = [role.person.displayName, role.person.pgaId, role.title.title];

  return searchableValues.some((value) => value?.toLowerCase().includes(normalizedSearch));
}

export function getEffectiveGovernanceSearch(
  searchValue: string,
  debouncedSearch: string,
  searchParam?: string
): string {
  if (searchValue === "" && (searchParam == null || searchParam === "")) {
    return "";
  }

  return debouncedSearch;
}

interface ClearGovernanceFiltersArgs {
  setShowArchived: (value: boolean) => void;
  setSearchValue: (value: string) => void;
  deleteSearchParams: (key: string | string[]) => void;
}

export function clearGovernanceFilters({
  setShowArchived,
  setSearchValue,
  deleteSearchParams
}: ClearGovernanceFiltersArgs) {
  setShowArchived(false);
  setSearchValue("");
  deleteSearchParams(["showArchived", "search"]);
}
